import type { AuthUser } from '../api/auth';

/**
 * Check if user is an admin
 */
export function isAdmin(user: AuthUser | null): boolean {
  return user?.is_admin === true;
}

/**
 * Check if user has premium access
 * Checks if user is premium AND subscription is still active
 */
export function isPremium(user: AuthUser | null): boolean {
  if (!user) return false;
  
  // Admins always have access
  if (user.is_admin === true) return true;
  
  // Check if user has premium flag
  if (user.is_premium !== true) return false;
  
  // Check subscription status
  const status = user.subscription_status;
  if (status === 'canceled' || status === 'inactive' || status === 'past_due') {
    // If canceled/inactive, check if they still have time left
    if (user.subscription_end_date) {
      const endDate = new Date(user.subscription_end_date);
      const now = new Date();
      return now < endDate; // Still premium until end date
    }
    return false;
  }
  
  // Active subscription
  return status === 'active';
}

/**
 * Check if user has any unlocks remaining
 * Returns false for premium users (they have unlimited access)
 */
export function hasUnlocksRemaining(user: AuthUser | null): boolean {
  if (!user) return false;
  // Premium users have unlocks_remaining: null, which means unlimited
  if (user.unlocks_remaining === null) return true;
  return (user.unlocks_remaining ?? 0) > 0;
}

/**
 * Get number of unlocks remaining
 * Returns null for premium users (unlimited), 0 for users with no unlocks
 */
export function getUnlocksRemaining(user: AuthUser | null): number | null {
  if (!user) return 0;
  return user.unlocks_remaining ?? 0;
}

/**
 * Check if user can access premium features
 * Combines admin check with active subscription check
 */
export function canAccessFeature(user: AuthUser | null, feature: 'recipes' | 'portions' | 'builder'): boolean {
  if (!user) return false;
  
  // Use isPremium which already checks admin status and active subscription
  return isPremium(user);
}

/**
 * Check if subscription is canceled but still has active premium access until end date
 */
export function isCanceledButActive(user: AuthUser | null): boolean {
  if (!user) return false;
  
  if (user.subscription_status === 'active' && user.subscription_end_date) {
    const endDate = new Date(user.subscription_end_date);
    const now = new Date();
    return now < endDate;
  }
  
  return false;
}

/**
 * Get formatted subscription end date
 */
export function getSubscriptionEndDate(user: AuthUser | null): string | null {
  if (!user?.subscription_end_date) return null;
  
  const endDate = new Date(user.subscription_end_date);
  return endDate.toLocaleDateString('en-US', { 
    year: 'numeric',
    month: 'long', 
    day: 'numeric' 
  });
}

/**
 * Get days remaining until subscription ends
 */
export function getDaysUntilSubscriptionEnd(user: AuthUser | null): number | null {
  if (!user?.subscription_end_date) return null;
  
  // Parse dates in UTC to avoid timezone issues
  const endDate = new Date(user.subscription_end_date);
  const endDateUTC = Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth(), endDate.getUTCDate());
  
  const now = new Date();
  const nowUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  
  const diffTime = endDateUTC - nowUTC;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays > 0 ? diffDays : 0;
}

/**
 * Get user role display name
 */
export function getUserRole(user: AuthUser | null): string {
  if (!user) return 'Guest';
  if (user.is_admin) return 'Admin';
  if (user.is_premium) return 'Premium';
  return 'Free';
}
